import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from './supabase';
import { useAuth } from './auth-context';

interface BrandingContextType {
  primaryColor: string;
  loading: boolean;
  companyName?: string;
  companyEmail?: string;
  companyPhone?: string;
  companyAddress?: string;
  logoUrl?: string;
}

const BrandingContext = createContext<BrandingContextType>({
  primaryColor: '#2E6F40',
  loading: true,
  companyName: '',
  companyEmail: '',
  companyPhone: '',
  companyAddress: '',
  logoUrl: '',
});

export const useBranding = () => useContext(BrandingContext);

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { tenantId, currentOrg } = useAuth();
  const effectiveTenantId = tenantId || currentOrg?.id || null;
  const [primaryColor, setPrimaryColor] = useState('#2E6F40');
  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [logoUrl, setLogoUrl] = useState('');

  useEffect(() => {
    loadBranding();
  }, [effectiveTenantId]);

  const loadBranding = async () => {
    if (!effectiveTenantId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('branding')
        .select('primary_color, company_name, email, phone, address, logo_url')
        .eq('tenant_id', effectiveTenantId)
        .maybeSingle();

      if (error) {
        // branding table may not exist yet — silently use defaults
        return;
      }

      if (data && data.primary_color) {
        setPrimaryColor(data.primary_color);
      }

      if (data) {
        setCompanyName(data.company_name || '');
        setCompanyEmail(data.email || '');
        setCompanyPhone(data.phone || '');
        setCompanyAddress(data.address || '');
        setLogoUrl(data.logo_url || '');
      }
    } catch (error) {
      console.error('Error loading branding:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BrandingContext.Provider value={{ primaryColor, loading, companyName, companyEmail, companyPhone, companyAddress, logoUrl }}>
      {children}
    </BrandingContext.Provider>
  );
}
